"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Zap, Cog, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const services = [{
  id: "preventive",
  title: "Önleyici Bakım",
  description: "Endüstriyel kapılarınızın ömrünü uzatan ve arızaları önceden engelleyen periyodik bakım hizmetleri.",
  icon: ShieldCheck,
  details: [{
    q: "Periyodik Kontrol Neleri Kapsar?",
    a: "Mekanik aksamların yağlanması, sensör kalibrasyonları, motor performans testleri ve genel güvenlik denetimlerini içerir."
  }, {
    q: "Bakım Sıklığı Ne Olmalıdır?",
    a: "Kapının kullanım yoğunluğuna ve ortam şartlarına bağlı olarak genellikle 3, 6 veya 12 aylık periyotlarla yapılması önerilir."
  }, {
    q: "Raporlama Yapılıyor mu?",
    a: "Evet, her bakım sonrası kapının durumu, değişmesi gereken parçalar ve yapılan işlemler hakkında detaylı teknik rapor sunulur."
  }]
}, {
  id: "emergency",
  title: "Acil Onarım",
  description: "Beklenmedik arızalarda iş sürekliliğinizi sağlamak için hızlı müdahale ve kesin çözüm garantisi.",
  icon: Zap,
  details: [{
    q: "Müdahale Süresi Nedir?",
    a: "Acil servis çağrılarında uzman teknik ekibimiz, lokasyonunuza en kısa sürede ulaşarak soruna yerinde müdahale eder."
  }, {
    q: "Geçici Çözüm Sunuluyor mu?",
    a: "Parça tedariği gerektiren büyük arızalarda, tesis güvenliğini ve asgari işleyişi sağlayacak geçici mekanik çözümler üretilir."
  }, {
    q: "Hangi Markalara Hizmet Veriliyor?",
    a: "Öncelikli olarak resmi temsilcisi olduğumuz Butzbach sistemlerine ve diğer önde gelen endüstriyel kapı markalarına hizmet veriyoruz."
  }]
}, {
  id: "upgrade",
  title: "Sistem Yükseltmeleri",
  description: "Mevcut kapı sistemlerinizi en son teknolojiler ve orijinal yedek parçalarla modernize edin.",
  icon: Cog,
  details: [{
    q: "Orijinal Parça Garantisi Var mı?",
    a: "Tüm yedek parça değişimlerinde ve sistem yükseltmelerinde %100 orijinal, garantili Butzbach ürünleri kullanılmaktadır."
  }, {
    q: "Otomasyon Yükseltmesi Mümkün mü?",
    a: "Eski manuel veya yarı otomatik sistemlerinize yeni nesil radar, fotosel ve akıllı uzaktan kontrol modülleri entegre edilebilir."
  }, {
    q: "Enerji Verimliliği Sağlar mı?",
    a: "Yeni nesil motorlar ve yalıtım panelleri ile yapılan yükseltmeler, tesisinizin enerji kaybını önemli ölçüde azaltır."
  }]
}];
export default function TeknikServisVeBakimHizmetleri() {
  const headerReveal = useRevealOnScroll();
  return <section id="teknik-servis-ve-bakım-hizmetleri" className="dark bg-background text-foreground py-24 px-4 md:px-8 lg:px-12 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
        <div className="absolute top-[60%] -right-[10%] w-[40%] h-[60%] rounded-lg bg-accent/5 blur-[100px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div ref={headerReveal.ref} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Teknik Servis ve <span className="text-primary">Bakım</span>
          </h2>
          <p className="font-sans text-lg text-muted-foreground">
            TÜR TOR TEKNİK olarak, Butzbach kalitesini kesintisiz yaşamanız için
            uzman kadromuzla kapsamlı servis, bakım ve orijinal yedek parça
            çözümleri sunuyoruz.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {services.map((service, index) => {
          const cardReveal = useRevealOnScroll({
            threshold: 0.1
          });
          const Icon = service.icon;
          return <div key={service.id} data-index={index} ref={cardReveal.ref} className={`relative flex flex-col bg-card border border-border/50 rounded-2xl p-8 shadow-lg overflow-hidden transition-all duration-700 hover:border-primary/30 hover:shadow-primary/5 group ${cardReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${index * 150}ms`
          }}>
                {}
                <Icon className="absolute -right-8 -bottom-8 w-64 h-64 text-primary/[0.03] transition-transform duration-700 group-hover:scale-110 group-hover:text-primary/[0.05] pointer-events-none" />

                <div className="relative z-10 flex-grow flex flex-col">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 text-primary">
                    <Icon className="w-7 h-7" />
                  </div>

                  <h3 className="font-default text-2xl font-semibold mb-4">
                    {service.title}
                  </h3>
                  <p className="font-sans text-muted-foreground mb-8 min-h-[80px]">
                    {service.description}
                  </p>

                  <div className="mt-auto">
                    <h4 className="font-default text-sm font-semibold text-foreground/80 uppercase tracking-wider mb-4 border-b border-border/50 pb-2">
                      Teknik Detaylar
                    </h4>
                    <Accordion type="single" collapsible className="w-full">
                      {service.details.map((detail, detailIndex) => <AccordionItem key={detailIndex} data-index={detailIndex} value={`item-${detailIndex}`} className="border-border/50">
                          <AccordionTrigger className="text-left font-sans hover:text-primary transition-colors">
                            {detail.q}
                          </AccordionTrigger>
                          <AccordionContent className="font-sans text-muted-foreground leading-relaxed">
                            {detail.a}
                          </AccordionContent>
                        </AccordionItem>)}
                    </Accordion>
                  </div>
                </div>
              </div>;
        })}
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" className="rounded-lg px-8">
            <Link to="/MaintenanceAndRepairPage">
              Tüm Servis Detaylarını İncele
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}